
import React from 'react';

interface LocationStepProps {
  /** The current step data */
  data: any;
  /** Callback to update the step data */
  updateData: (data: any) => void;
  /** Callback to move to the next step */
  nextStep: () => void;
}

/**
 * Renders the location selection step.
 *
 * The component is deliberately defensive:
 *  - It validates the `updateData` and `nextStep` callbacks before invoking them.
 *  - It catches any unexpected errors while updating data or navigating forward,
 *    logging them to the console (or you could forward them to a global error handler).
 */
export default function LocationStep({
  data,
  updateData,
  nextStep,
}: LocationStepProps) {
  /**
   * Handles the user choosing a location.
   *
   * @param location The selected location type.
   */
  const handleSelect = (location: 'garage' | 'domicile') => {
    try {
      // Guard against missing callbacks – this makes the component more robust
      if (typeof updateData !== 'function' || typeof nextStep !== 'function') {
        throw new Error('Required callbacks are not functions');
      }

      // Update the shared data model
      updateData({
        location,
        address: '',
        postalCode: '',
        city: '',
        distance: 0,
        travelCost: 0,
      });

      // Advance to the next step
      nextStep();
    } catch (err) {
      // In a real app you might show a toast or report to an error tracking service
      console.error('LocationStep.handleSelect error:', err);
    }
  };

  return (
    <div className="bg-dark-card border border-dark-border rounded-2xl p-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">
          Où souhaitez-vous le service ?
        </h2>
        <p className="text-gray-400">Choisissez le lieu qui vous convient</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button
          onClick={() => handleSelect('garage')}
          className="p-8 rounded-xl border-2 border-dark-border hover:border-primary/50 transition-all duration-300 hover:scale-105 cursor-pointer"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-primary/20 rounded-full mb-4">
              <i className="ri-home-gear-line text-3xl text-primary"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Au Garage</h3>
            <p className="text-gray-400 text-sm mb-4">
              Venez directement à notre garage à Payerne
            </p>
            <div className="mt-4 text-primary font-bold">Gratuit</div>
          </div>
        </button>

        <button
          onClick={() => handleSelect('domicile')}
          className="p-8 rounded-xl border-2 border-dark-border hover:border-primary/50 transition-all duration-300 hover:scale-105 cursor-pointer"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-primary/20 rounded-full mb-4">
              <i className="ri-map-pin-line text-3xl text-primary"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Prise en Charge</h3>
            <p className="text-gray-400 text-sm mb-4">
              Nous venons chercher votre véhicule
            </p>
            <div className="mt-4 text-primary font-bold">1.50 CHF/km</div>
          </div>
        </button>
      </div>
    </div>
  );
}
